import { Button, Flex, Spacing, Switch, Text } from '@billy/design-system'
import { type ChangeEvent, type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useComponentId, useFormContext, useWatch } from '../../../../../../../../../../hooks'
import { InvoicingSettingsSection } from '../../../../../../../../enums/invoicingSettingsSection'
import type { AutomaticPaymentRemindersType } from '../../../../../../enums/automaticPaymentRemindersType'
import { useTrackAutomaticPaymentReminders } from '../../../../../../hooks/useTrackAutomaticPaymentReminders'
import type { PaymentsAndRemindersSettingsFormSchema } from '../../../../types/paymentsAndRemindersSettingsFormSchema'
import { automaticPaymentRemindersRowFieldPathsByType } from '../../constants/automaticPaymentRemindersRowFieldPathsByType'
import { automaticPaymentRemindersRowTKeysByType } from '../../constants/automaticPaymentRemindersRowTKeysByType'

interface AutomaticPaymentRemindersRowProps {
  hasPremiumSubscriptionPlan: boolean
  onEdit: (paymentRemindersType: AutomaticPaymentRemindersType) => void
  onUpgradeRequired: () => void
  paymentRemindersType: AutomaticPaymentRemindersType
}

export const AutomaticPaymentRemindersRow = ({
  hasPremiumSubscriptionPlan,
  onEdit,
  onUpgradeRequired,
  paymentRemindersType,
}: AutomaticPaymentRemindersRowProps): ReactElement => {
  const { t } = useTranslation()
  const { control, FormItem } = useFormContext<PaymentsAndRemindersSettingsFormSchema>()
  const titleId = useComponentId('automatic-payment-reminders-row-title')
  const { trackAutomaticPaymentReminders } = useTrackAutomaticPaymentReminders({
    section: InvoicingSettingsSection.PaymentsAndReminders,
  })

  const { active: activeFieldPath, nDays: nDaysFieldPath } =
    automaticPaymentRemindersRowFieldPathsByType[paymentRemindersType]
  const days = useWatch({ control, name: nDaysFieldPath })

  const { description, title } = automaticPaymentRemindersRowTKeysByType[paymentRemindersType]

  const handleEditButtonClick = useCallback(() => {
    onEdit(paymentRemindersType)
    trackAutomaticPaymentReminders('modal opened', paymentRemindersType)
  }, [onEdit, paymentRemindersType, trackAutomaticPaymentReminders])

  return (
    <Flex alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column">
        <Text id={titleId}>{t(title)}</Text>
        {description && (
          <Text colorVariant="secondary" variant="bodySM">
            {t(description, { days })}
          </Text>
        )}
      </Flex>
      <Flex alignItems="center" gap={Spacing.S12}>
        <Button aria-describedby={titleId} icon="pencil" onClick={handleEditButtonClick} size="sm" variant="tonal">
          {t('settings.organization.external_invoice.automatic_payment_reminders.edit')}
        </Button>
        <FormItem
          name={activeFieldPath}
          render={({ field: { onChange, value } }) => {
            const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
              if (!hasPremiumSubscriptionPlan) {
                onUpgradeRequired()
                return
              }

              onChange(event)

              if (!value) {
                trackAutomaticPaymentReminders('toggled on', paymentRemindersType)
              }
            }

            return (
              <Switch
                aria-labelledby={titleId}
                checked={!!value}
                disabled={!hasPremiumSubscriptionPlan}
                onChange={handleSwitchChange}
              />
            )
          }}
        />
      </Flex>
    </Flex>
  )
}
